import React, { ReactNode } from 'react'
import { SelectChangeEvent, Typography, Box } from '@mui/material'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'
import { Controller, useForm, UseFormReturn } from 'react-hook-form'
import { shouldDisplayError } from '../../utils/formErrorHandlers'

const defaultSx = (theme: any) => {
  return {
    Dropdown: {
      width: '100%',
      height: theme.spacing(5.375),
      borderRadius: theme.spacing(),
      boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.07)',
      '&.Mui-focused': {
        '.MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: `0px 1px 6px 0px ${theme.palette.primary.main}`,
        },
      },
    },
    formControl: {
      marginBottom: theme.spacing(3),
      height: theme.spacing(6.125),
    },
    inputLabel: {
      top: '-5px',
      color: '#000000B2',
      '&.Mui-focused': {
        color: '#000000B2',
      },
    },
    mainInputStyle: {
      height: theme.spacing(5.25),
      '&.MuiSelect-select': {
        padding: '11.5px 14px',
        display: 'inline-flex',
        alignItems: 'center',
      },
    },
    optionsContainer: {
      marginTop: theme.spacing(0.625),
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0px 1px 6px 0px ${theme.palette.primary.main}`,
      borderRadius: '8px',
    },
  }
}

export type ItemObject = {
  value: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
  disabledText?: string
  className?: string
}

// NOTE:
// To access the addition properties of the https://mui.com/material-ui/react-text-field/ component
// in future, add the require property to the DropdownProps, e.g. maxRows

export interface DropdownProp {
  disabled?: boolean
  error?: boolean
  form?: UseFormReturn
  helperText?: string
  id?: string
  items: Array<ItemObject>
  label?: string
  name: string
  menuProps?: object
  onBlur?: React.FocusEventHandler
  onKeyDown?: React.KeyboardEventHandler
  onKeyPress?: React.KeyboardEventHandler
  onChange?: Function
  required?: boolean
  rules?: object
  sx?: any
  value?: string
  width?: number
}

const Dropdown = (props: DropdownProp) => {
  const theme = useTheme()
  const styles = defaultSx(theme)

  const handlerOnChange = (
    event: SelectChangeEvent<string>,
    onChangeHook: Function,
  ) => {
    onChangeHook(event)
    props.onChange && props.onChange(event)
  }

  const rules = {
    required: props.required && `${props.label || 'Field'} is required`,
    validate: {
      ...props.rules,
    },
  }

  const useFormDefault = props.form || useForm()
  const {
    formState: { errors },
  } = useFormDefault

  const getDefaultValue = () => {
    //Set the passed value only if it exist in the list of items
    return props.value && props.items.find(item => item.value == props.value)
      ? props.value
      : null
  }
  // Controller: Wrapper component to make easier to work with Material-UI external controlled component.
  // React Hook Form embraces uncontrolled components and native inputs.
  // https://blog.logrocket.com/react-hook-form-complete-guide/
  const formControl = props.sx?.formControl || {}

  return (
    <Controller
      control={useFormDefault.control}
      defaultValue={getDefaultValue}
      name={props.name}
      render={({ field: { onChange: onChangeHook, value, name, ref } }) => (
        <FormControl
          error={shouldDisplayError(errors, name, props.error)}
          fullWidth
          sx={{ ...styles.formControl, ...formControl }}
        >
          {!value && (
            <InputLabel shrink={false} sx={styles.inputLabel}>
              {props.label}
            </InputLabel>
          )}
          <Select
            disabled={props.disabled}
            error={shouldDisplayError(errors, name, props.error)}
            id={props.id || props.name}
            inputProps={{
              sx: styles.mainInputStyle,
              id: `selectInput${props.id || props.name}`,
            }}
            MenuProps={{
              sx: {
                '.MuiMenu-paper': styles.optionsContainer,
              },
              ...props.menuProps,
            }}
            name={name || ''}
            onChange={(event: SelectChangeEvent<string>) =>
              handlerOnChange(event, onChangeHook)
            }
            sx={{
              ...styles.Dropdown,
              ...props.sx,
            }}
            value={value || ''}
          >
            {props.items?.map((item: ItemObject, index: number) => (
              <MenuItem
                className='dropdownItem'
                key={index}
                value={item.value}
                disabled={item.disabled}
                sx={{
                  display: 'block',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                  {item.className && <div className={item.className} />}
                  <ListItemText sx={{ display: 'inline-block' }}>
                    {item.label}
                  </ListItemText>
                </Box>
                {item.disabled && item.disabledText && (
                  <Typography
                    sx={{
                      color: '#00000099',
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      lineHeight: 1.125,
                    }}
                  >
                    {item.disabledText}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      rules={rules}
    />
  )
}

export default Dropdown
